import { useState } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Dialog from "@radix-ui/react-dialog";
import retirementAssetsIllustration from "../../static/illustration--retirement-assets.svg";
import { useAnswer } from "../../util/form";
import { useAppStore } from "../../store/store";

export default function OtherRetirementAccounts({
  advanceScreen,
  previousScreen,
  validation,
  updateValidation,
  screen,
}) {
  const { user, formOptions } = useAppStore();
  const { answer, setAnswer } = useAnswer("outside-assets");
  const [assets, setAssets] = useState(
    answer.outsideAssets?.length > 0 ? [...answer.outsideAssets] : [],
  );
  const [accountType, setAccountType] = useState(1);
  const [accountName, setAccountName] = useState(null);
  const [accountValue, setAccountValue] = useState(null);
  const USDollar = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  function renderAssetList() {
    return assets.map((asset) => {
      return (
        <div
          key={asset.id}
          className="w-full px-4 py-5 text-sm transition-all duration-200 ease-in-out border rounded-lg shadow-sm border-as-green-100 md:px-6 md:py-7"
        >
          <div className="flex justify-between font-semibold text-gray-800">
            <span>{assetTypebyId(asset.outsideAssetId)}</span>
            <span className="">{USDollar.format(asset.value)}</span>
          </div>
          <div className="flex justify-between">
            <span>{asset.name}</span>

            <button
              onClick={() => removeAsset(asset.id)}
              className="font-medium text-teal-800 underline hover:text-as-green-100"
            >
              Delete
            </button>
          </div>
        </div>
      );
    });
  }

  function assetTypebyId(assetId) {
    const asset = formOptions.assets.find(
      (asset) => String(asset.id) === String(assetId),
    );
    let name = null;
    if (asset) name = asset.name;
    return name;
  }

  function removeAsset(id) {
    const newAssetArray = assets.filter((asset) => asset.id !== id);
    setAssets(newAssetArray);
  }

  // eslint-disable-next-line no-unused-vars
  function updateAnswer(value) {
    setAnswer(value);
    if (value) {
      updateValidation(true);
    } else {
      updateValidation(false);
    }
  }

  function getTotalValue() {
    let amountTotal = 0;
    assets.forEach((asset) => {
      amountTotal += parseFloat(asset.value);
    });
    return amountTotal;
  }

  function renderTotalValue() {
    const amountTotal = getTotalValue();
    if (amountTotal > 0) updateValidation(true);
    else updateValidation(false);
    return USDollar.format(amountTotal);
  }

  function addNewAsset() {
    let lastAssetID = 0;
    if (assets.length > 0) lastAssetID = assets[assets.length - 1].id;
    setAssets([
      ...assets,
      {
        id: lastAssetID + 1,
        outsideAssetId: accountType,
        name: accountName,
        value: accountValue || 0,
      },
    ]);
    setAccountName("");
    setAccountValue(null);
    setAccountType("1");
  }

  return (
    <motion.div
      className="w-full h-full"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
    >
      <div className="px-6 py-16 text-center md:py-20 md:px-16">
        <div className="max-w-3xl mx-auto">
          <img
            className="block h-auto mx-auto w-28 md:w-36"
            src={retirementAssetsIllustration}
            alt=""
          />
          <h1 className="mt-6 text-3xl md:mt-8 md:text-4xl">
            Let’s include your other retirement assets.
          </h1>
          <div className="max-w-xl mx-auto">
            <p className="mt-3 text-base text-gray-600 md:mt-5">
              Add items such as Previous 403(b), Roth IRA, Annuity, etc.
            </p>
          </div>
        </div>
        <div className="mt-12 mb-16 md:mb-24 md:mt-16 mx-auto max-w-[37.5rem]">
          <div className="space-y-3 md:space-y-4">
            {renderAssetList()}
            <Dialog.Root onOpenChange={(isOpen) => {
                if (isOpen) {
                  setAccountType(1)
                  setAccountName("");
                  setAccountValue(null);
                }
              }}>
              <Dialog.Trigger asChild>
                <button
                  className="w-full p-6 border-2 border-gray-300 border-dashed rounded shadow-none md:p-9 hover:border-gray-400 hover:text-gray-900 focus-visible:border-gray-400 focus-visible:text-gray-900 active:border-gray-400 active:text-gray-900 disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400"
                >
                  <FontAwesomeIcon
                    icon="fa-solid fa-plus"
                    className="mr-2 text-teal-700"
                  />
                  <span className="text-sm font-medium text-gray-600">
                    Add Asset
                  </span>
                </button>
              </Dialog.Trigger>
              <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-[100] animate-fade-in overflow-y-auto overflow-x-hidden bg-teal-900/80">
                  <div className="wrapper">
                    <Dialog.Content className="w-full max-w-md mx-auto my-16 bg-white rounded-md shadow-xl lg:my-24">
                      <div className="px-6 pt-6 pb-11">
                        <Dialog.Title className="mb-6 text-2xl text-gray-800 md:mb-8">
                          Add Account Asset
                        </Dialog.Title>
                        <div className="flex flex-col space-y-5">
                          <div>
                            <label
                              className="block mb-1 text-sm font-bold text-gray-800"
                              htmlFor="accountType"
                            >
                              What is the account type?
                            </label>
                            <select
                              id="accountType"
                              name="accountType"
                              value={accountType}
                              onChange={(e) => setAccountType(e.target.value)}
                              required
                              aria-label="What is the account type?"
                              className="w-full px-3 py-2 mb-0 text-base text-gray-500 border-0 rounded shadow-sm ring-1 ring-inset ring-as-green-100 placeholder:text-gray-300 focus:ring-2 focus:ring-inset focus:ring-as-green-50"
                            >
                              {formOptions.assets.map((option) => (
                                <option key={option.id} value={option.id}>
                                  {option.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label
                              className="block mb-1 text-sm font-bold text-gray-800"
                              htmlFor="accountName"
                            >
                              What is the account name?
                            </label>
                            <input
                              type="text"
                              id="accountName"
                              name="accountName"
                              value={accountName}
                              placeholder="Account Nickname"
                              onChange={(e) => {
                                setAccountName(e.target.value);
                              }}
                              required
                              aria-label="What is the account name?"
                              className="w-full px-3 py-2 mb-0 text-base text-gray-500 border-0 rounded shadow-sm ring-1 ring-inset ring-as-green-100 placeholder:text-gray-300 focus:ring-2 focus:ring-inset focus:ring-as-green-50"
                            />
                          </div>
                          <div>
                            <label
                              className="block mb-1 text-sm font-bold text-gray-800"
                              htmlFor="accountValue"
                            >
                              How much is in the account?
                            </label>
                            <input
                              type="text"
                              id="accountValue"
                              name="accountValue"
                              value={accountValue}
                              onKeyPress={(e) => {
                                const charCode = e.which ? e.which : e.keyCode;
                                if (
                                  charCode > 31 &&
                                  (charCode < 48 || charCode > 57) &&
                                  charCode !== 46
                                ) {
                                  e.preventDefault();
                                }
                              }}
                              onChange={(e) => {
                                const val = e.target.value;
                                setAccountValue(val);
                              }}
                              required
                              aria-label="How much is in the account?"
                              className="w-full px-3 py-2 mb-0 text-base text-gray-500 border-0 rounded shadow-sm ring-1 ring-inset ring-as-green-100 placeholder:text-gray-300 focus:ring-2 focus:ring-inset focus:ring-as-green-50"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="p-6 border-t border-t-gray-200">
                        <div className="flex gap-3 md:justify-end md:gap-5">
                          <Dialog.Close asChild>
                            <button className="btn btn--secondary btn--small">
                              Cancel
                            </button>
                          </Dialog.Close>
                          <Dialog.Close asChild>
                            <button
                              onClick={addNewAsset}
                              className="btn btn--primary btn--small"
                              disabled={Number(accountValue) <= 0}
                            >
                              Save
                            </button>
                          </Dialog.Close>
                        </div>
                      </div>
                    </Dialog.Content>
                  </div>
                </Dialog.Overlay>
              </Dialog.Portal>
            </Dialog.Root>
          </div>

          <div className="mt-4 md:mt-6 flex flex-wrap items-center justify-between gap-1.5 border-t border-gray-200 pt-4">
            <span className="text-sm leading-normal text-gray-600">
              Total Value of Outside Assets
            </span>

            <span className="text-lg font-bold leading-tight text-gray-600">
              {renderTotalValue()}
            </span>
          </div>
        </div>
        <div className="flex justify-center gap-3 mt-6 md:mt-10 md:gap-5">
          <button className="btn btn--secondary" onClick={previousScreen}>
            Back
          </button>
          <button
            className="btn btn--primary"
            disabled={Boolean(validation) === false}
            onClick={() => {
              // Save outside assets to form state before advancing
              const totalOutsideAssets = getTotalValue();
              setAnswer({
                outsideAssets: assets, // Save full asset array for internal use
                gbOutsideAssets: totalOutsideAssets, // Save total for API call
              });
              advanceScreen();
            }}
          >
            Continue
          </button>
        </div>
      </div>
    </motion.div>
  );
}
